'use client'
import { Icon } from '@/components/common'
import Link from '@/features/ref/components/Link'
import { getTimeFromNow } from '@/utils/date-time'
import { Box, Divider, IconButton, Skeleton, Stack, styled, Typography } from '@mui/material'
import { useTranslations } from 'next-intl'
import Image from 'next/image'
import { Navigation, Pagination } from 'swiper/modules'
import { Swiper, SwiperSlide } from 'swiper/react'
import { IListPostReviewItem } from '../../types/review'
import { ContainerCustomV2 } from '@/components/common/container-custom-v2'

const StyledBtnArrowSlide = styled(IconButton)(({ theme }) => ({
  color: 'inherit',
  ':hover': {
    backgroundColor: theme.palette.action.hover,
  },
}))
const StyledPagination = styled(Box)(({ theme }) => ({
  position: 'static',
  textAlign: 'center',
  margin: '16px auto 0',
  '& .swiper-pagination-bullet': {
    width: '8px',
    height: '8px',
    background: 'transparent',
    border: `1px solid #BBBBBA`,
    opacity: 1,
    margin: '0 2px',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    '&.swiper-pagination-bullet-active': {
      background: theme.palette.primary.main,
      width: '24px',
      borderRadius: '999px',
    },
  },
}))

const StyledSwiper = styled(Swiper)(() => ({
  overflow: 'visible',
  '& .swiper-wrapper': {
    overflow: 'visible',
  },
}))

// Skeleton component for ReviewItem
const ReviewItemSkeleton = () => {
  return (
    <Stack spacing={1} direction={'row'}>
      <Skeleton
        variant="rectangular"
        sx={{
          aspectRatio: '4/3',
          flexShrink: 0,
          width: { xs: '130px', lg: '108px', xxl: '130px' },
          borderRadius: '8px',
        }}
      />
      <Stack spacing={1} justifyContent={'center'} flex={1}>
        <Skeleton variant="text" width="90%" height={20} />
        <Skeleton variant="text" width="60%" height={20} />
        <Skeleton variant="text" width="40%" height={16} />
      </Stack>
    </Stack>
  )
}
const StyledSwiperSlide = styled(SwiperSlide)(({ theme }) => ({
  cursor: 'grab',
  [theme.breakpoints.up('lg')]: {
    maxWidth: '25vw',
  },
}))

// List Post review
export const ListPostReview = ({
  data,
  isLoading,
}: {
  data: IListPostReviewItem[] | undefined
  isLoading?: boolean
}) => {
  const t = useTranslations()

  if ((!data || data?.length === 0) && !isLoading) {
    return null
  }
  // const tempList = chunk(data || [], 2)
  const tempList = data

  return (
    <Box
      component={'section'}
      sx={{
        scrollMarginTop: {
          xs: '80px',
          lg: '120px',
        },
      }}
    >
      <ContainerCustomV2>
        <Stack direction={'row'} justifyContent={'space-between'} alignItems={'center'} mb={4}>
          <Typography
            variant="h3"
            sx={{
              fontSize: { xs: '1.25rem', md: '1.75rem', xl: '2rem', xxl: '2.5rem' },
              lineHeight: 1.3,
              fontWeight: 600,
            }}
          >
            {t('Product.detail.review-list')}
          </Typography>
          <Stack direction={'row'} spacing={1} alignItems={'center'} sx={{ color: 'primary.main' }}>
            <StyledBtnArrowSlide className="swiper-button-prev-post-review">
              <Icon.ArrowRight sx={{ color: 'inherit', fontSize: '14px', transform: 'rotate(180deg)' }} />
            </StyledBtnArrowSlide>
            <Divider
              orientation="vertical"
              sx={{ background: '#BBBBBA', height: '12px', my: 'auto!important' }}
              flexItem
            />
            <StyledBtnArrowSlide className="swiper-button-next-post-review">
              <Icon.ArrowRight sx={{ color: 'inherit', fontSize: '14px' }} />
            </StyledBtnArrowSlide>
          </Stack>
        </Stack>
      </ContainerCustomV2>

      {isLoading ? (
        <Stack spacing={2}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <Stack spacing={2} flex={1}>
              <ReviewItemSkeleton />
              <ReviewItemSkeleton />
            </Stack>
            <Stack spacing={2} flex={1} sx={{ display: { xs: 'none', md: 'flex' } }}>
              <ReviewItemSkeleton />
              <ReviewItemSkeleton />
            </Stack>
          </Stack>
        </Stack>
      ) : (
        <StyledSwiper
          modules={[Pagination, Navigation]}
          navigation={{
            enabled: true,
            nextEl: '.swiper-button-next-post-review',
            prevEl: '.swiper-button-prev-post-review',
          }}
          spaceBetween={16}
          // centeredSlides={tempList && tempList?.length < 5}
          breakpoints={{
            0: {
              slidesPerView: 2.2,
            },
            768: {
              slidesPerView: 3.2,
            },
            1024: {
              slidesPerView: 4.2,
            },
          }}
          loop={true}
          observer={true}
          observeParents={true}
          pagination={{
            clickable: true,
            type: 'bullets',
            el: '.swiper-pagination-custom',
          }}
          sx={{
            '& .swiper-wrapper': {
              justifyContent: tempList && tempList?.length < 5 ? 'center' : 'initial',
            },
          }}
        >
          {/* nhân đôi chuỗi để không lỗi loop và center */}
          {[...(tempList || []), ...(tempList && tempList.length < 5 ? tempList : [])].map(
            (review, index) => {
              return (
                <StyledSwiperSlide key={`${review?.publish_date}-${index}`}>
                  <ReviewItem review={review} />
                </StyledSwiperSlide>
              )
            }
          )}
          <StyledPagination className="swiper-pagination-custom"></StyledPagination>
        </StyledSwiper>
      )}
    </Box>
  )
}

const ReviewItem = ({ review }: { review: IListPostReviewItem }) => {
  return (
    <Link href={`/blog/${review?.url_key}`} target="_blank" rel="noopener noreferrer">
      <Stack spacing={1}>
        <Box
          sx={{
            aspectRatio: '4/3',
            flexShrink: 0,
            // width: { xs: '130px', lg: '108px', xxl: '130px' },
            objectFit: 'cover',
            borderRadius: { xs: '4px', md: '8px' },
            overflow: 'hidden',
            width: '100%',
          }}
        >
          <Image
            src={review?.image}
            alt={review?.name}
            width={128}
            height={96}
            sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 25vw"
            style={{ aspectRatio: '4/3', objectFit: 'cover', width: '100%', height: '100%' }}
            quality={90}
            loading="lazy"
          />
        </Box>
        <Stack spacing={1} justifyContent={'center'}>
          <Typography
            variant="body2"
            fontWeight={600}
            sx={{
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {review?.name}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              color: '#6D6C6B',
              display: '-webkit-box',
              WebkitLineClamp: 1,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {getTimeFromNow(review?.publish_date, 0)}
          </Typography>
        </Stack>
      </Stack>
    </Link>
  )
}
